import { Controller } from "@hotwired/stimulus"

const SVG_NS = "http://www.w3.org/2000/svg"
const XLINK_NS = "http://www.w3.org/1999/xlink"
const FIT_MARGIN = 56
const NODE_R = 30
const NODE_RING = NODE_R + 2
const NODE_GAP = 28
const MIN_CENTER_DIST = 2 * NODE_RING + NODE_GAP
const EDGE_HIDE_SCALE = 0.45

const STAT_TIPS = {
  nodes:
    "People who have at least one accepted friendship and appear on this map (network members, not every registered account).",
  edges:
    "Accepted friendship links between those people. Each friendship is counted once.",
  avgDegree:
    "On average, how many friends each person in the network has. Higher values mean more connections per person.",
  density:
    "How close the network is to “everyone knows everyone,” from 0 (sparse) to 1 (fully connected). Computed as actual links divided by the maximum possible links.",
  components:
    "How many separate groups exist where there is no friend-path between groups. One component means the whole map is connected.",
  largestComponent:
    "Number of people in the biggest connected group—the main cluster where everyone can reach everyone else through friends.",
  avgPath:
    "Average shortest path length between pairs in the largest connected group—how many friend-hops it typically takes to go from one person to another (related to “six degrees of separation”).",
  medianPath:
    "Middle value of all shortest paths in the main group. Less affected by a few very long paths than the average.",
  diameter:
    "The longest shortest path in the main group—the maximum number of friend-hops between any two people in that cluster.",
  clustering:
    "How often a person’s friends are also friends with each other. Higher values mean tighter, more clustered local circles (small-world style grouping)."
}

export default class extends Controller {
  static values = {
    url: String,
    layout: String
  }

  static targets = ["stats"]

  connect() {
    const host = this.#graphHost()
    host.innerHTML = ""
    host.classList.add("friend-network-graph-host")
    this.transform = { x: 0, y: 0, scale: 1 }
    this.isPanning = false
    this.panStart = null
    this.rafId = null

    this.boundThemeChange = () => this.#applyPalette()
    this.themeObserver = new MutationObserver(this.boundThemeChange)
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"]
    })

    this.boundWheel = this.#onWheel.bind(this)
    this.boundPointerDown = this.#onPointerDown.bind(this)
    this.boundPointerMove = this.#onPointerMove.bind(this)
    this.boundPointerUp = this.#onPointerUp.bind(this)
    this.boundDoubleClick = () => this.fitToView()

    this.load()
  }

  disconnect() {
    this.themeObserver?.disconnect()
    this.#detachInteraction()
    if (this.rafId) cancelAnimationFrame(this.rafId)
  }

  #graphHost() {
    return this.element.querySelector(".app-card.min-h-52") || this.element
  }

  #escapeAttr(text) {
    return String(text)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
  }

  #escapeHtml(text) {
    return this.#escapeAttr(text).replace(/>/g, "&gt;")
  }

  #setMessage(text) {
    this.#detachInteraction()
    const host = this.#graphHost()
    host.innerHTML = ""
    host.classList.add("friend-network-graph-host")
    host.textContent = text
  }

  #graphPalette() {
    const dark = document.documentElement.classList.contains("dark")
    return dark
      ? {
          line: "#475569",
          ringFill: "#18181b",
          ringStroke: "#52525b",
          discFill: "#3f3f46",
          labelFill: "#e4e4e7",
          focusRing: "focus:ring-teal-500"
        }
      : {
          line: "#cbd5e1",
          ringFill: "#ffffff",
          ringStroke: "#e5e7eb",
          discFill: "#e5e7eb",
          labelFill: "#374151",
          focusRing: "focus:ring-teal-500"
        }
  }

  async load() {
    const res = await fetch(this.urlValue, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    })

    if (!res.ok) {
      this.#setMessage("Could not load the network map.")
      return
    }

    const data = await res.json()
    const nodes = data.nodes || []
    const links = data.links || []

    if (nodes.length === 0) {
      this.#setMessage("No users to show yet.")
      return
    }

    this.width = data.width || 920
    this.height = data.height || 520
    this.graphData = { nodes, links }

    this.#renderStats(data.stats)
    this.#buildSvg(nodes, links)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.fitToView())
    })
  }

  #renderStats(stats) {
    if (!this.hasStatsTarget || !stats) return

    const fmt = (v, digits = 2) =>
      v == null ? "—" : Number(v).toFixed(digits)

    const rows = [
      { tip: STAT_TIPS.nodes, label: "Users in network", value: stats.nodes_count },
      { tip: STAT_TIPS.edges, label: "Friendships", value: stats.edges_count },
      { tip: STAT_TIPS.avgDegree, label: "Average degree", value: fmt(stats.avg_degree) },
      { tip: STAT_TIPS.density, label: "Network density", value: fmt(stats.density, 4) },
      {
        tip: STAT_TIPS.components,
        label: "Connected components",
        value: stats.components_count
      },
      {
        tip: STAT_TIPS.largestComponent,
        label: "Largest component",
        value: stats.largest_component_size
      },
      {
        tip: STAT_TIPS.avgPath,
        label: "Avg. degrees of separation",
        value: fmt(stats.avg_path_length),
        note:
          stats.nodes_outside_giant_component > 0
            ? ` (${stats.largest_component_size} users in main group)`
            : ""
      },
      {
        tip: STAT_TIPS.medianPath,
        label: "Median degrees of separation",
        value: fmt(stats.median_path_length)
      },
      {
        tip: STAT_TIPS.diameter,
        label: "Maximum (diameter)",
        value: stats.diameter ?? "—"
      },
      {
        tip: STAT_TIPS.clustering,
        label: "Clustering coefficient",
        value: fmt(stats.avg_clustering_coefficient, 4)
      }
    ]

    this.statsTarget.innerHTML = rows
      .map(
        ({ tip, label, value, note = "" }, index) =>
          `<div class="friend-network-stat-row" tabindex="0" role="group" data-tip="${this.#escapeAttr(tip)}" aria-describedby="friend-network-stat-tip-${index}">
            <span class="sr-only" id="friend-network-stat-tip-${index}">${this.#escapeHtml(label)}: ${this.#escapeHtml(tip)}</span>
            <dt>
              <span class="friend-network-stat-label">${label}</span>
              <span class="friend-network-stat-hint" aria-hidden="true">?</span>
            </dt>
            <dd>${value}${note}</dd>
          </div>`
      )
      .join("")
    this.statsTarget.closest("[data-stats-panel]")?.classList.remove("hidden")
  }

  #buildSvg(nodes, links) {
    this.#detachInteraction()
    const host = this.#graphHost()
    host.innerHTML = ""
    host.classList.remove("friend-network-graph-host")
    host.classList.add("friend-network-graph-wrap")

    const shell = document.createElement("div")
    shell.className = "friend-network-graph-shell"

    const toolbar = document.createElement("div")
    toolbar.className = "friend-network-graph-toolbar"
    toolbar.innerHTML = `
      <button type="button" class="btn-secondary text-xs px-2 py-1" data-action="friend-network-graph#zoomIn">Zoom in</button>
      <button type="button" class="btn-secondary text-xs px-2 py-1" data-action="friend-network-graph#zoomOut">Zoom out</button>
      <button type="button" class="btn-secondary text-xs px-2 py-1" data-action="friend-network-graph#fitToView">Fit</button>
    `
    shell.appendChild(toolbar)

    const svg = document.createElementNS(SVG_NS, "svg")
    svg.setAttribute("viewBox", `0 0 ${this.width} ${this.height}`)
    svg.setAttribute("role", "img")
    svg.setAttribute("aria-label", "Friend network map")
    svg.classList.add("friend-network-graph-svg")

    const defs = document.createElementNS(SVG_NS, "defs")
    svg.appendChild(defs)

    const viewport = document.createElementNS(SVG_NS, "g")
    viewport.setAttribute("class", "friend-network-graph-viewport")

    const palette = this.#graphPalette()

    const gLines = document.createElementNS(SVG_NS, "g")
    gLines.setAttribute("class", "friend-network-graph-edges")
    gLines.setAttribute("stroke", palette.line)
    gLines.setAttribute("stroke-width", "2")
    gLines.setAttribute("pointer-events", "none")

    for (const link of links) {
      const a = this.#pos(nodes, link.source)
      const b = this.#pos(nodes, link.target)
      if (!a || !b) continue
      const dist = Math.hypot(b.x - a.x, b.y - a.y)
      if (dist < MIN_CENTER_DIST * 0.95) continue
      const { x1, y1, x2, y2 } = this.#linkEndpoints(a, b)
      const line = document.createElementNS(SVG_NS, "line")
      line.setAttribute("x1", x1)
      line.setAttribute("y1", y1)
      line.setAttribute("x2", x2)
      line.setAttribute("y2", y2)
      line.dataset.source = String(link.source)
      line.dataset.target = String(link.target)
      gLines.appendChild(line)
    }
    viewport.appendChild(gLines)

    const gNodes = document.createElementNS(SVG_NS, "g")
    gNodes.setAttribute("class", "friend-network-graph-nodes")

    for (const n of nodes) {
      const p = this.#pos(nodes, n.id)
      const anchor = document.createElementNS(SVG_NS, "a")
      anchor.setAttribute("href", n.profileUrl)
      anchor.setAttribute("class", "friend-network-graph-node")
      anchor.dataset.nodeId = String(n.id)

      const ring = document.createElementNS(SVG_NS, "circle")
      ring.setAttribute("class", "fn-ring")
      ring.setAttribute("cx", p.x)
      ring.setAttribute("cy", p.y)
      ring.setAttribute("r", NODE_RING)
      ring.setAttribute("fill", palette.ringFill)
      ring.setAttribute("stroke", palette.ringStroke)
      ring.setAttribute("stroke-width", "2")
      anchor.appendChild(ring)

      if (n.avatarUrl) {
        const clipId = `fn-clip-${n.id}`
        const clip = document.createElementNS(SVG_NS, "clipPath")
        clip.setAttribute("id", clipId)
        const clipCircle = document.createElementNS(SVG_NS, "circle")
        clipCircle.setAttribute("cx", p.x)
        clipCircle.setAttribute("cy", p.y)
        clipCircle.setAttribute("r", NODE_R)
        clip.appendChild(clipCircle)
        defs.appendChild(clip)

        const disc = document.createElementNS(SVG_NS, "circle")
        disc.setAttribute("class", "fn-disc")
        disc.setAttribute("cx", p.x)
        disc.setAttribute("cy", p.y)
        disc.setAttribute("r", NODE_R)
        disc.setAttribute("fill", palette.discFill)
        anchor.appendChild(disc)

        const img = document.createElementNS(SVG_NS, "image")
        img.setAttribute("class", "fn-avatar")
        img.setAttribute("href", n.avatarUrl)
        img.setAttributeNS(XLINK_NS, "href", n.avatarUrl)
        img.setAttribute("x", p.x - NODE_R)
        img.setAttribute("y", p.y - NODE_R)
        img.setAttribute("width", NODE_R * 2)
        img.setAttribute("height", NODE_R * 2)
        img.setAttribute("clip-path", `url(#${clipId})`)
        img.setAttribute("preserveAspectRatio", "xMidYMid slice")
        img.addEventListener("error", () => {
          img.remove()
          this.#appendInitialLabel(anchor, n, p, palette)
        })
        anchor.appendChild(img)
      } else {
        this.#appendInitialLabel(anchor, n, p, palette)
      }

      const title = document.createElementNS(SVG_NS, "title")
      title.textContent = n.username
      anchor.appendChild(title)

      anchor.addEventListener("mouseenter", () => this.#highlightNode(n.id))
      anchor.addEventListener("mouseleave", () => this.#clearHighlight())

      gNodes.appendChild(anchor)
    }
    viewport.appendChild(gNodes)

    svg.appendChild(viewport)
    shell.appendChild(svg)
    host.appendChild(shell)

    this.svg = svg
    this.viewport = viewport
    this.edgeGroup = gLines

    svg.addEventListener("wheel", this.boundWheel, { passive: false })
    svg.addEventListener("pointerdown", this.boundPointerDown)
    svg.addEventListener("pointermove", this.boundPointerMove)
    svg.addEventListener("pointerup", this.boundPointerUp)
    svg.addEventListener("pointerleave", this.boundPointerUp)
    svg.addEventListener("dblclick", this.boundDoubleClick)
  }

  #pos(nodes, id) {
    const n = nodes.find((node) => String(node.id) === String(id))
    if (!n || n.x == null) return null
    return { x: n.x, y: n.y }
  }

  #appendInitialLabel(anchor, node, position, palette) {
    if (anchor.querySelector(".fn-label")) return

    if (!anchor.querySelector(".fn-disc")) {
      const disc = document.createElementNS(SVG_NS, "circle")
      disc.setAttribute("class", "fn-disc")
      disc.setAttribute("cx", position.x)
      disc.setAttribute("cy", position.y)
      disc.setAttribute("r", NODE_R)
      disc.setAttribute("fill", palette.discFill)
      anchor.insertBefore(disc, anchor.querySelector(".fn-avatar"))
    }

    const txt = document.createElementNS(SVG_NS, "text")
        txt.setAttribute("class", "fn-label")
        txt.setAttribute("x", position.x)
        txt.setAttribute("y", position.y + 6)
        txt.setAttribute("text-anchor", "middle")
        txt.setAttribute("pointer-events", "none")
        txt.setAttribute("fill", palette.labelFill)
    txt.setAttribute("font-size", "18")
    txt.setAttribute("font-weight", "600")
    txt.textContent = node.initial || "?"
    anchor.appendChild(txt)
  }

  #screenToSvg(clientX, clientY) {
    const pt = this.svg.createSVGPoint()
    pt.x = clientX
    pt.y = clientY
    const matrix = this.svg.getScreenCTM()
    if (!matrix) return { x: 0, y: 0 }
    const mapped = pt.matrixTransform(matrix.inverse())
    return { x: mapped.x, y: mapped.y }
  }

  #detachInteraction() {
    if (!this.svg) return
    this.svg.removeEventListener("wheel", this.boundWheel)
    this.svg.removeEventListener("pointerdown", this.boundPointerDown)
    this.svg.removeEventListener("pointermove", this.boundPointerMove)
    this.svg.removeEventListener("pointerup", this.boundPointerUp)
    this.svg.removeEventListener("pointerleave", this.boundPointerUp)
    this.svg.removeEventListener("dblclick", this.boundDoubleClick)
  }

  #applyPalette() {
    if (!this.svg) return
    const palette = this.#graphPalette()
    this.edgeGroup?.setAttribute("stroke", palette.line)
    this.svg.querySelectorAll(".fn-ring").forEach((el) => {
      el.setAttribute("fill", palette.ringFill)
      el.setAttribute("stroke", palette.ringStroke)
    })
    this.svg.querySelectorAll(".fn-disc").forEach((el) => {
      el.setAttribute("fill", palette.discFill)
    })
    this.svg.querySelectorAll(".fn-label").forEach((el) => {
      el.setAttribute("fill", palette.labelFill)
    })
  }

  #applyTransform() {
    if (!this.viewport) return
    const { x, y, scale } = this.transform
    this.viewport.setAttribute(
      "transform",
      `translate(${x} ${y}) scale(${scale})`
    )
    if (this.edgeGroup) {
      this.edgeGroup.style.display =
        scale < EDGE_HIDE_SCALE ? "none" : ""
    }
  }

  #scheduleTransform() {
    if (this.rafId) return
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null
      this.#applyTransform()
    })
  }

  #graphPointFromScreen(clientX, clientY) {
    const svgPt = this.#screenToSvg(clientX, clientY)
    const { scale, x, y } = this.transform
    return {
      x: (svgPt.x - x) / scale,
      y: (svgPt.y - y) / scale
    }
  }

  #onWheel(event) {
    event.preventDefault()
    const factor = event.deltaY < 0 ? 1.1 : 1 / 1.1
    this.#zoomAt(event.clientX, event.clientY, factor)
  }

  zoomIn() {
    const rect = this.svg.getBoundingClientRect()
    this.#zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, 1.2)
  }

  zoomOut() {
    const rect = this.svg.getBoundingClientRect()
    this.#zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, 1 / 1.2)
  }

  #zoomAt(clientX, clientY, factor) {
    const anchor = this.#screenToSvg(clientX, clientY)
    const before = this.#graphPointFromScreen(clientX, clientY)
    const nextScale = Math.min(4, Math.max(0.08, this.transform.scale * factor))
    this.transform.scale = nextScale
    this.transform.x = anchor.x - before.x * nextScale
    this.transform.y = anchor.y - before.y * nextScale
    this.#scheduleTransform()
  }

  #onPointerDown(event) {
    if (event.target.closest(".friend-network-graph-node")) return
    event.preventDefault()
    this.isPanning = true
    this.panStart = {
      clientX: event.clientX,
      clientY: event.clientY,
      tx: this.transform.x,
      ty: this.transform.y
    }
    this.svg.classList.add("is-panning")
    this.svg.setPointerCapture(event.pointerId)
  }

  #onPointerMove(event) {
    if (!this.isPanning || !this.panStart) return
    event.preventDefault()
    const start = this.#screenToSvg(this.panStart.clientX, this.panStart.clientY)
    const now = this.#screenToSvg(event.clientX, event.clientY)
    this.transform.x = this.panStart.tx + (now.x - start.x)
    this.transform.y = this.panStart.ty + (now.y - start.y)
    this.#scheduleTransform()
  }

  #onPointerUp(event) {
    if (!this.isPanning) return
    this.isPanning = false
    this.panStart = null
    this.svg.classList.remove("is-panning")
    try {
      this.svg.releasePointerCapture(event.pointerId)
    } catch {
      /* already released */
    }
  }

  fitToView() {
    if (!this.graphData?.nodes?.length || !this.svg) return

    const pad = NODE_RING + NODE_GAP + 8
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity

    for (const n of this.graphData.nodes) {
      minX = Math.min(minX, n.x - pad)
      minY = Math.min(minY, n.y - pad)
      maxX = Math.max(maxX, n.x + pad)
      maxY = Math.max(maxY, n.y + pad)
    }

    const graphW = maxX - minX || 1
    const graphH = maxY - minY || 1
    const scale = Math.min(
      (this.width - FIT_MARGIN * 2) / graphW,
      (this.height - FIT_MARGIN * 2) / graphH
    )
    const cx = (minX + maxX) / 2
    const cy = (minY + maxY) / 2

    this.transform = {
      scale,
      x: this.width / 2 - cx * scale,
      y: this.height / 2 - cy * scale
    }
    this.#applyTransform()
  }

  #highlightNode(nodeId) {
    const id = String(nodeId)
    this.edgeGroup?.querySelectorAll("line").forEach((line) => {
      const match =
        line.dataset.source === id || line.dataset.target === id
      line.setAttribute("stroke-opacity", match ? "1" : "0.12")
      line.setAttribute("stroke-width", match ? "3" : "2")
    })
  }

  #clearHighlight() {
    this.edgeGroup?.querySelectorAll("line").forEach((line) => {
      line.setAttribute("stroke-opacity", "1")
      line.setAttribute("stroke-width", "2")
    })
  }

  #linkEndpoints(a, b) {
    const dx = b.x - a.x
    const dy = b.y - a.y
    const dist = Math.hypot(dx, dy)
    const ux = dx / dist
    const uy = dy / dist
    return {
      x1: a.x + ux * NODE_RING,
      y1: a.y + uy * NODE_RING,
      x2: b.x - ux * NODE_RING,
      y2: b.y - uy * NODE_RING
    }
  }
}
